import { MODULE_GROUPS, MODULES, type NotificationItem, type PlatformModule } from '@erp/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  Bell,
  Building2,
  CheckCheck,
  ClipboardList,
  Cog,
  Cpu,
  DatabaseZap,
  FileCheck2,
  Gauge,
  HardHat,
  Headset,
  House,
  LogOut,
  Menu,
  MessageSquare,
  Palmtree,
  Route,
  Users,
  UtensilsCrossed,
  Workflow,
  X,
} from 'lucide-react';
import { type ComponentType, useCallback, useState } from 'react';
import { api } from '@/lib/api';
import { useCan, useLogout, useUser } from '@/lib/auth';
import { crmCountersQuery } from '@/lib/crm';
import { helpdeskCountersQuery } from '@/lib/equipment';
import { inboxCountersQuery, taskCountersQuery } from '@/lib/queries';
import { useRealtime } from '@/lib/realtime';
import { safetyCountersQuery } from '@/lib/safety';
import { Avatar } from './ui';

const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  org: Building2,
  employees: Users,
  tasks: ClipboardList,
  approvals: CheckCheck,
  requests: FileCheck2,
  crm: Gauge,
  vacations: Palmtree,
  safety: HardHat,
  meals: UtensilsCrossed,
  production: Workflow,
  equipment: Cpu,
  helpdesk: Headset,
};

function NavItem({
  to,
  icon: Icon,
  label,
  sprint,
  badge,
  alert,
  comments,
  onNavigate,
}: {
  to: string;
  icon: ComponentType<{ className?: string }>;
  label: string;
  sprint?: number;
  /** Число справа: задачи в работе и т. п. */
  badge?: number;
  /** Требует внимания (есть просроченные) — число подсвечивается */
  alert?: boolean;
  /** Новые комментарии — отдельная отметка перед основным числом */
  comments?: number;
  onNavigate?: () => void;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'group relative flex h-9 items-center gap-3 rounded-md px-3 text-[14px] transition-colors',
        active ? 'bg-graphite-2 text-white' : 'text-[#b9c5ce] hover:bg-graphite-2/60 hover:text-white',
        sprint && !active && 'text-[#7e8d98]',
      )}
    >
      {active && <span aria-hidden className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-signal" />}
      <Icon className="size-[18px] shrink-0 opacity-90" />
      <span className="min-w-0 truncate">{label}</span>
      {sprint && (
        <span
          className="ml-auto shrink-0 whitespace-nowrap text-[11.5px] tabular-nums text-[#6f7f8b]"
          title={`Появится в спринте ${sprint}`}
        >
          спр. {sprint}
        </span>
      )}
      {!sprint && comments ? (
        <span
          className={clsx(
            'inline-flex shrink-0 items-center gap-0.5 rounded-full bg-signal px-1.5 text-[11.5px] font-semibold leading-[18px] text-ink tabular-nums',
            'ml-auto',
          )}
          title={`Новых комментариев: ${comments}`}
        >
          <MessageSquare className="size-3" aria-hidden />
          {comments}
          <span className="sr-only">новых комментариев</span>
        </span>
      ) : null}
      {!sprint && badge ? (
        <span
          className={clsx(
            'min-w-[20px] shrink-0 rounded-full px-1.5 text-center text-[11.5px] font-semibold leading-[18px] tabular-nums',
            !comments && 'ml-auto',
            comments && 'ml-1',
            alert ? 'bg-danger text-white' : 'bg-graphite-line text-[#d5dde3]',
          )}
        >
          {badge}
        </span>
      ) : null}
    </Link>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const can = useCan();
  const counters = useQuery(taskCountersQuery);
  const inbox = useQuery(inboxCountersQuery);
  const crm = useQuery({ ...crmCountersQuery, enabled: can('crm.access') });
  const crmAttention = (crm.data?.unreadChats ?? 0) + (crm.data?.overdueActivities ?? 0);
  const helpdesk = useQuery({
    ...helpdeskCountersQuery,
    enabled: can('helpdesk.access') || can('helpdesk.ticket.create'),
  });
  const safety = useQuery(safetyCountersQuery);
  const safetyMine = (safety.data?.unsigned ?? 0) + (safety.data?.conduct ?? 0);
  const badges: Record<string, number | undefined> = {
    safety: safetyMine || undefined,
    tasks: counters.data?.doing,
    approvals: inbox.data?.open,
    crm: crmAttention || undefined,
    helpdesk: (helpdesk.data?.mine ?? 0) + (helpdesk.data?.free ?? 0) || undefined,
  };
  const visible = (m: PlatformModule) => !m.anyOf || m.anyOf.some((code) => can(code));
  const groups = Object.entries(MODULE_GROUPS) as [PlatformModule['group'], string][];
  return (
    <nav aria-label="Разделы" className="flex h-full flex-col gap-5 overflow-y-auto px-3 py-4">
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5 px-2">
        <img src="/favicon.svg" alt="" className="size-8" />
        <span className="text-[17px] font-semibold tracking-[-0.01em] text-white">ДизельПро</span>
      </Link>
      <div className="flex flex-col gap-0.5">
        <NavItem to="/" icon={House} label="Главная" onNavigate={onNavigate} />
      </div>
      {groups.map(([group, title]) => (
        <div key={group} className="flex flex-col gap-0.5">
          <p className="px-3 pb-1 text-[12px] font-medium text-[#7e8d98]">{title}</p>
          {MODULES.filter((m) => m.group === group && visible(m)).map((m) => (
            <NavItem
              key={m.code}
              to={m.path}
              icon={ICONS[m.code] ?? Cog}
              label={m.title}
              sprint={m.status === 'planned' ? m.sprint : undefined}
              badge={badges[m.code]}
              comments={m.code === 'tasks' ? counters.data?.unreadComments || undefined : undefined}
              alert={
                (m.code === 'tasks' && Boolean(counters.data?.overdue)) ||
                (m.code === 'crm' && crmAttention > 0) ||
                (m.code === 'helpdesk' && Boolean(helpdesk.data?.overdue)) ||
                (m.code === 'safety' && safetyMine > 0)
              }
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ))}
      {(can('access.manage') || can('bpm.settings.manage') || can('migration.import')) && (
        <div className="mt-auto flex flex-col gap-0.5 border-t border-graphite-line pt-4">
          {can('access.manage') && (
            <NavItem to="/admin/access" icon={Cog} label="Учётки и роли" onNavigate={onNavigate} />
          )}
          {can('bpm.settings.manage') && (
            <NavItem to="/admin/routes" icon={Route} label="Маршруты" onNavigate={onNavigate} />
          )}
          {can('migration.import') && (
            <NavItem
              to="/admin/migration"
              icon={DatabaseZap}
              label="Перенос из Битрикс24"
              onNavigate={onNavigate}
            />
          )}
        </div>
      )}
    </nav>
  );
}

function NotificationsButton() {
  const { data } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => api<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 120_000,
  });
  const count = data?.count ?? 0;
  return (
    <Link
      to="/notifications"
      className="relative inline-flex size-9 items-center justify-center rounded-md text-ink-soft hover:bg-sunken hover:text-ink"
      aria-label={count ? `Уведомления, непрочитанных: ${count}` : 'Уведомления'}
    >
      <Bell className="size-[19px]" />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-graphite px-1 text-center text-[11px] font-semibold leading-[18px] text-white ring-2 ring-paper">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </Link>
  );
}

function Toast({ item, onClose }: { item: NotificationItem; onClose: () => void }) {
  return (
    <div
      role="status"
      className="fixed bottom-5 right-5 z-50 w-[340px] panel p-4 shadow-[0_8px_24px_-8px_rgb(24_34_43/0.35)] animate-[toast-in_180ms_ease-out]"
    >
      <div className="flex items-start gap-3">
        <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full bg-signal" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{item.title}</p>
          {item.body && <p className="mt-0.5 text-steel">{item.body}</p>}
          {item.link && (
            <Link to={item.link} onClick={onClose} className="mt-2 inline-block link text-[13px]">
              Открыть
            </Link>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Закрыть" className="text-steel hover:text-ink">
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

export function AppShell() {
  const user = useUser();
  const logout = useLogout();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toast, setToast] = useState<NotificationItem | null>(null);
  const showToast = useCallback((n: NotificationItem) => {
    setToast(n);
    window.setTimeout(() => setToast((t) => (t?.id === n.id ? null : t)), 8000);
  }, []);
  useRealtime(true, showToast);
  const name = user.employee?.fullName ?? user.login;

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[256px_1fr]">
      <aside className="hidden bg-graphite lg:sticky lg:top-0 lg:block lg:h-dvh">
        <Sidebar />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Закрыть меню"
            className="absolute inset-0 bg-ink/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-[272px] bg-graphite">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-paper/90 px-4 backdrop-blur lg:px-8">
          <button
            type="button"
            className="inline-flex size-9 items-center justify-center rounded-md hover:bg-sunken lg:hidden"
            aria-label="Открыть меню"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="size-5" />
          </button>
          <div className="ml-auto flex items-center gap-1">
            <NotificationsButton />
            <Link
              to="/profile"
              className="flex items-center gap-2.5 rounded-md py-1 pl-1.5 pr-2.5 hover:bg-sunken"
            >
              <Avatar name={name} userId={user.id} size={30} />
              <span className="hidden max-w-[220px] truncate text-[13.5px] sm:block">{name}</span>
            </Link>
            <button
              type="button"
              onClick={logout}
              className="inline-flex size-9 items-center justify-center rounded-md text-ink-soft hover:bg-sunken hover:text-ink"
              aria-label="Выйти"
              title="Выйти"
            >
              <LogOut className="size-[18px]" />
            </button>
          </div>
        </header>
        <main className="px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
      {toast && <Toast item={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
